import { onLCP, onCLS, onTTFB, onFCP, onINP } from 'web-vitals/attribution';
import { EventToolbox } from 'client/utils/event-toolbox';
import { MissingClassnamesFromCriticalCss } from 'client/engagement-handlers/performance-engagement-handler/find-missing-critical-css-classes';

let sendImmediately;
let bufferedMetrics;
let alreadyFiredOnLoad;
let venomTest;
let xTraceId;

/**
 * the module vars are set from this init function as opposed to directly in the module so that they can be reset
 * from one unit test block to another
 */
function initModuleVars() {
  sendImmediately = false;
  bufferedMetrics = {
    webVitals: {
      CLS: [],
      INP: [],
    },
  };
}

function getPerformanceNavigationTiming() {
  // performance is not supported until Safari 11
  if (!performance || typeof performance.getEntriesByType !== 'function') {
    return null;
  }
  const [performanceNavigationTiming] = performance.getEntriesByType('navigation');
  // performanceNavigationTiming is not supported in Safari and IE
  if (!performanceNavigationTiming) {
    return null;
  }
  return performanceNavigationTiming;
}

function sendToAnalytics(metrics = bufferedMetrics) {
  const metricsStr = JSON.stringify(metrics);
  const venomTestStr = JSON.stringify(venomTest);
  const data = {
    event_type: 'action_completed',
    event_data: {
      action_category: 'system',
      action_name: 'site_performance',
      metrics: metricsStr,
      ...venomTest,
      value: `{"venomTest":${venomTestStr}}`,
    },
  };
  const dataV2 = {
    event_type: 'action_completed',
    event_data: {
      action_category: 'system',
      action_name: 'site_performance_v2',
      // todo after legacy pixel is no longer fired replace this with value: JSON.stringify({ venomTest, metrics })
      value: `{"venomTest":${venomTestStr},"metrics":${metricsStr},"xTraceId":${xTraceId}`,
    },
  };

  if (!alreadyFiredOnLoad) {
    data.event_data.action_cause = 'page_load';
    dataV2.event_data.action_cause = 'page_load';
    alreadyFiredOnLoad = true;
  } else {
    data.event_data.action_cause = 'data_update';
    dataV2.event_data.action_cause = 'data_update';
  }

  EventToolbox.fireTrackAction(data);
  EventToolbox.fireTrackAction(dataV2);
}

function logLcp(timingMetrics) {
  const {
    lcpEntry,
    ttfb,
    lcpRequestStart,
    lcpResponseEnd,
    lcpRenderTime,
    resourceLoadDelay,
    resourceLoadDuration,
    elementRenderDelay,
  } = timingMetrics;
  console.log('LCP element: ', lcpEntry.element, lcpEntry.url); // eslint-disable-line no-console
  console.log('LCP url: ', lcpEntry.url); // eslint-disable-line no-console

  const measures = [
    ['ttfb', ttfb, 0],
    ['LCP Resource Request Start', lcpRequestStart, resourceLoadDelay],
    ['LCP Resource Response End', lcpResponseEnd, resourceLoadDuration],
    ['LCP', lcpRenderTime, elementRenderDelay],
  ];

  const table = measures.map(measure => ({
    name: measure[0],
    timing: measure[1],
    'delay(diff from prev time)': measure[2],
  }));

  console.table(table); // eslint-disable-line no-console

  // create visualization in chrome dev performance
  const LCP_SUB_PARTS = [
    ['Time to first byte', 0, ttfb],
    ['Resource load delay', ttfb, lcpRequestStart],
    ['Resource load duration', lcpRequestStart, lcpResponseEnd],
    ['Element render delay', lcpResponseEnd, lcpRenderTime],
  ];
  LCP_SUB_PARTS.forEach(part => {
    performance.clearMeasures(part[0]);
    performance.measure(part[0], {
      start: part[1],
      end: part[2],
    });
  });
}

function processINPEntries(entries) {
  return entries.map(entry => {
    const inputDelay = entry.processingStart - entry.startTime;
    const procTime = entry.processingEnd - entry.processingStart;
    const presentationDelay = entry.duration - (inputDelay + procTime);
    return {
      name: entry.name,
      procTime: Math.round(procTime),
      inputDelay: Math.round(inputDelay),
      presentationDelay: Math.round(presentationDelay),
    };
  });
}

function logInp(data) {
  const entries = processINPEntries(data.entries);
  const filteredEntries = entries.filter((value, index) => {
    const valueString = JSON.stringify(value);
    return index === entries.findIndex(obj => JSON.stringify(obj) === valueString);
  });
  const baseStyle = `font-weight:bold`;
  const deltaStyle = `${baseStyle};color:${data.delta < 100 ? '#71df69' : '#e5473c'}`;
  const valueStyle = `${baseStyle};color:${data.value < 200 ? '#71df69' : '#e5473c'}`;
  /* istanbul ignore next */
  /* eslint-disable no-console */
  console.groupCollapsed(
    `[performance-engagement-handler] INP value: %c${data.value} ms (${data.rating})%c, Delta from last: %c${
      data.delta
    } ms`,
    valueStyle,
    '',
    deltaStyle
  );

  console.log('INP element:', data.entries[0].target);
  filteredEntries.forEach(entry => {
    const { procTime, inputDelay, presentationDelay, name } = entry;
    const measures = [
      ['Input delay', inputDelay],
      ['Processing Time', procTime],
      ['Presentation Delay', presentationDelay],
    ];
    const table = measures.map(measure => ({
      'INP sub-part': measure[0],
      'Time (ms)': measure[1],
    }));

    console.log('Event type:', name);
    console.table(table);
  });
  console.groupEnd();
  /* eslint-enable no-console */
}

/**
 * @param {Object} attribution - attribution object from web-vitals package event
 * @param {function} callback - callback to send lcp subparts to
 * @param {boolean} debugLcp - flag for logging lcp metrics in console
 */
export function getLCPSubparts(attribution, callback, debugLcp = false) {
  const isLCPSubpartsSupported = !!attribution;
  if (!isLCPSubpartsSupported) {
    return;
  }

  const { lcpEntry, lcpResourceEntry } = attribution;
  if (!lcpEntry) {
    return;
  }

  // ignore LCP entries that aren't images to reduce DevTools noise
  // comment this line out if you want to include text entries
  if (!lcpEntry.url) return;

  // compute start and end times of each LCP sub-part
  const ttfb = lcpResourceEntry?.timeToFirstByte;

  // if LCP resource is loaded cross-origin, add Timing-Allow-Origin (TAO) header to get the most accurate results
  // requestStart (if TAO is set), otherwise use startTime
  // TAO "is set" when requestStart is non-0
  const lcpRequestStart = lcpResourceEntry?.requestStart || (lcpResourceEntry?.startTime ?? 0);
  const lcpResponseEnd = lcpResourceEntry?.responseEnd ?? 0;

  if (callback) {
    const lcpSubParts = {
      resourceRequestStart: lcpRequestStart,
      resourceResponseEnd: lcpResponseEnd,
    };
    callback(lcpSubParts);
  }

  if (debugLcp) {
    const lcpRenderTime = lcpEntry.renderTime;
    logLcp({ ...attribution, lcpEntry, ttfb, lcpRequestStart, lcpResponseEnd, lcpRenderTime });
  }
}

function handleEmitPerfMetrics() {
  const performanceNavigationTiming = getPerformanceNavigationTiming();
  if (!performanceNavigationTiming) {
    return;
  }

  alreadyFiredOnLoad = false;
  bufferedMetrics.performanceNavigationTiming = performanceNavigationTiming;
  // core web-vitals: CLS, FCP, LCP, TTFB, and INP
  onCLS(
    metrics => {
      const { entries, attribution, ...other } = metrics;
      if (!sendImmediately) {
        bufferedMetrics.webVitals.CLS.push(other);
        return;
      }
      sendToAnalytics({
        webVitals: {
          CLS: other,
        },
      });
    },
    { reportAllChanges: true }
  );
  onINP(
    metrics => {
      const { entries, ...other } = metrics;
      if (!sendImmediately) {
        bufferedMetrics.webVitals.INP.push(other);
        return;
      }
      sendToAnalytics({
        webVitals: {
          INP: other,
        },
      });
    },
    { reportAllChanges: true }
  );
  onFCP(metrics => {
    const { value } = metrics;
    bufferedMetrics.webVitals.FCP = value;
  });
  onLCP(
    metrics => {
      const { value, attribution } = metrics;
      if (!sendImmediately) {
        bufferedMetrics.webVitals.LCP = value;
      }
      getLCPSubparts(attribution, lcpMetrics => {
        bufferedMetrics.lcpSubParts = lcpMetrics;
      });
    },
    { reportAllChanges: true }
  );
  onTTFB(metrics => {
    const { value } = metrics;
    if (!sendImmediately) {
      bufferedMetrics.webVitals.TTFB = value;
      return;
    }
    sendToAnalytics({
      webVitals: {
        TTFB: value,
      },
    });
  });

  window.setTimeout(() => {
    sendImmediately = true;
    sendToAnalytics();
  }, 10);
}

export const PerformanceEngagementHandler = {
  /**
   * Sets up "pageLoad"" event listener when emitPerfMetrics is true
   * Reports on web vitals otherwise as denoted by available feature flags in redux state
   *
   * @param  {Object} getState Returns App redux store state (for feature flags)
   * @return {void}
   */
  init({ getState }) {
    if (!getState) {
      return;
    }
    initModuleVars();

    const state = getState();
    const {
      featureFlags: { emitPerfMetrics, debugCls, debugLcp, debugInp, debugCriticalCss },
    } = state;
    xTraceId = state.xHeaders['x-trace-id'];
    venomTest = state.pageContext?.server?.venomTest;

    if (emitPerfMetrics) {
      window.addEventListener('load', handleEmitPerfMetrics);
    }
    if (debugCriticalCss) {
      new MissingClassnamesFromCriticalCss().run();
    }
    if (debugCls) {
      console.log('[performance-engagement-handler] CLS score changes will be logged here'); // eslint-disable-line no-console
      onCLS(
        metrics => {
          /* istanbul ignore next */

          // eslint-disable-next-line no-console
          console.log('[performance-engagement-handler] CLS score change', {
            /* the ... is necessary cause from one call to another metrics points to same reference */
            ...metrics,
            entries: [...metrics.entries],
          });
        },
        { reportAllChanges: true }
      );
    }
    if (debugLcp) {
      console.log('[performance-engagement-handler] LCP subparts will be logged here'); // eslint-disable-line no-console
      onLCP(
        metrics => {
          const { attribution } = metrics;
          getLCPSubparts(attribution, undefined, debugLcp);
        },
        { reportAllChanges: true }
      );
    }
    if (debugInp) {
      onINP(
        metrics => {
          logInp(metrics);
        },
        { reportAllChanges: true }
      );
    }
  },
};
